/**
 * Home.js
 *
 * The landing page component.
 * 1. Hero          - who I am and what this site is (games you can play)
 * 2. Featured game - Auction, with how-to-play steps
 * 3. More games    - everything else from gamesWeHave
 * 4. Behind it     - short personal note from the developer
 */

import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Smartphone, Gavel, Trophy, VenetianMask, Drama } from "lucide-react";
import MyPhoto from "../assets/YashHome.JPG";
import { games } from "../assets/Games/gamesWeHave";

// Game cards fanned out in the hero (Auction sits in the middle)
const heroCards = [
    { name: "Trust Issues", icon: VenetianMask, color: "bg-lavender" },
    { name: "Auction",      icon: Gavel,        color: "bg-yellow" },
    { name: "Charades",     icon: Drama,        color: "bg-coral" },
];

// Mock bids shown in the Auction section
const sampleBids = [
    { team: "Team Blue",  amount: 180, color: "bg-blue" },
    { team: "Team Red",   amount: 220, color: "bg-red" },
    { team: "Team Green", amount: 260, color: "bg-green" },
];

const auctionSteps = [
    {
        icon: Users,
        title: "Host a room",
        text: "One person creates a room and runs the auction. Everyone else joins a team from their phone.",
    },
    {
        icon: Gavel,
        title: "Bid on anything",
        text: "Play a ready-made list or upload your own: movies, footballers, anything. Items go up one by one and the smartest bid takes it.",
    },
    {
        icon: Trophy,
        title: "Build the best lineup",
        text: "Highest-scoring team wins. The host sets the limits and bonus points before the game starts.",
    },
];

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.6, ease: "easeOut" },
};

const Home = () => {
    const otherGames = games.filter((game) => game.slug !== "auction");

    return (
        <div className="bg-black text-white overflow-hidden">

            {/* --- HERO --- */}
            <section className="min-h-screen max-w-6xl mx-auto px-6 pt-28 pb-16 flex flex-col lg:flex-row items-center gap-16">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="w-full lg:w-1/2 text-center lg:text-left"
                >
                    <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-6">
                        Party games by Yash Jain
                    </p>
                    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.05]">
                        Games made for <span className="text-yellow">game night.</span>
                    </h1>
                    <p className="mt-6 text-lg text-zinc-400 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                        I make games you play with friends in the same room. Everyone uses their own phone,
                        nobody downloads anything. Pick a game, start a room, and play.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                        <Link
                            to="/auction"
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-yellow text-black font-semibold px-6 py-3 hover:bg-white transition-colors"
                        >
                            Play Auction <ArrowRight size={18} />
                        </Link>
                        <Link
                            to="/games"
                            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-zinc-700 text-zinc-100 font-medium px-6 py-3 hover:bg-zinc-900 transition-colors"
                        >
                            Browse all games
                        </Link>
                    </div>
                </motion.div>

                {/* Fanned game cards */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    className="w-full lg:w-1/2 flex justify-center"
                >
                    <div className="relative w-56 h-80 md:w-64 md:h-96">
                        {heroCards.map(({ name, icon: Icon, color }, index) => (
                            <motion.div
                                key={name}
                                whileHover={{ y: -12 }}
                                className={`absolute inset-0 ${color} text-black rounded-3xl border-4 border-black shadow-2xl p-6 flex flex-col justify-between`}
                                style={{
                                    rotate: (index - 1) * 12,
                                    x: (index - 1) * 64,
                                    zIndex: index === 1 ? 3 : 1,
                                }}
                            >
                                <span className="text-xs font-bold uppercase tracking-widest">ISayYash Games</span>
                                <Icon size={96} strokeWidth={1.5} className="self-center" />
                                <span className="text-2xl font-extrabold tracking-tight">{name}</span>
                            </motion.div>
                        ))}
                        <motion.div
                            initial={{ scale: 2, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 1, type: "spring", stiffness: 300, damping: 15 }}
                            className="absolute -bottom-6 left-1/2 -translate-x-1/2 z-10"
                        >
                            <div className="rotate-[-6deg] rounded-xl border-2 border-yellow bg-black px-5 py-2 text-yellow font-extrabold tracking-widest text-lg shadow-xl">
                                SOLD!
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </section>

            {/* --- FEATURED GAME: AUCTION --- */}
            <section className="border-t border-zinc-900 py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div {...fadeUp}>
                            <p className="text-xs uppercase tracking-[0.25em] text-yellow mb-4">Featured game</p>
                            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">Auction</h2>
                            <p className="mt-4 text-lg text-zinc-400 leading-relaxed">
                                Bid, bluff and build your dream lineup of movies, players, or anything you like.
                                Every team gets the same budget, every item goes to the highest bidder, and
                                there's never quite enough money for everything you want.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3 text-sm text-zinc-300">
                                <span className="inline-flex items-center gap-2 rounded-full border border-zinc-800 px-3 py-1">
                                    <Users size={14} /> Best with a group
                                </span>
                                <span className="inline-flex items-center gap-2 rounded-full border border-zinc-800 px-3 py-1">
                                    <Smartphone size={14} /> Play on your phone
                                </span>
                            </div>
                        </motion.div>

                        {/* Mock live bidding panel */}
                        <motion.div {...fadeUp} className="rounded-3xl border border-zinc-800 bg-zinc-950 p-6">
                            <div className="flex items-center justify-between text-sm text-zinc-500 mb-6">
                                <span className="font-mono">LOT 07</span>
                                <span className="inline-flex items-center gap-2 text-red">
                                    <span className="w-2 h-2 rounded-full bg-red animate-pulse" /> Live
                                </span>
                            </div>
                            <div className="space-y-3">
                                {sampleBids.map(({ team, amount, color }, index) => (
                                    <motion.div
                                        key={team}
                                        initial={{ opacity: 0, x: 20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.3 + index * 0.4 }}
                                        className={`flex items-center justify-between rounded-xl border px-4 py-3 ${
                                            index === sampleBids.length - 1 ? "border-yellow bg-yellow/10" : "border-zinc-800"
                                        }`}
                                    >
                                        <span className="inline-flex items-center gap-3">
                                            <span className={`w-3 h-3 rounded-full ${color}`} />
                                            {team}
                                        </span>
                                        <span className="font-mono font-semibold">{amount}</span>
                                    </motion.div>
                                ))}
                            </div>
                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 1.8 }}
                                className="mt-6 text-center text-zinc-400 tracking-widest uppercase text-sm"
                            >
                                Going once… going twice…
                            </motion.p>
                        </motion.div>
                    </div>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {auctionSteps.map(({ icon: Icon, title, text }, index) => (
                            <motion.div
                                key={title}
                                {...fadeUp}
                                transition={{ ...fadeUp.transition, delay: index * 0.1 }}
                                className="rounded-2xl border border-zinc-800 bg-zinc-950 p-6"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="text-sm font-mono text-zinc-500">0{index + 1}</span>
                                    <Icon size={20} className="text-yellow" />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">{title}</h3>
                                <p className="text-zinc-400 leading-relaxed">{text}</p>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div {...fadeUp} className="mt-12">
                        <Link
                            to="/auction"
                            className="inline-flex items-center gap-2 rounded-2xl bg-yellow text-black font-semibold px-6 py-3 hover:bg-white transition-colors"
                        >
                            Start a room <ArrowRight size={18} />
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* --- MORE GAMES --- */}
            <section className="border-t border-zinc-900 py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                        <div>
                            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">The shelf</p>
                            <h2 className="text-4xl font-extrabold tracking-tight">More games</h2>
                        </div>
                        <Link to="/games" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
                            See all games <ArrowRight size={16} />
                        </Link>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {otherGames.map((game, index) => {
                            const content = (
                                <>
                                    <div className="flex items-center justify-between mb-6">
                                        <span
                                            className={`text-xs font-medium rounded-full px-3 py-1 ${
                                                game.active ? "bg-green/15 text-green" : "bg-zinc-800 text-zinc-400"
                                            }`}
                                        >
                                            {game.status}
                                        </span>
                                        {game.active && (
                                            <ArrowRight size={18} className="text-zinc-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
                                        )}
                                    </div>
                                    <h3 className="text-2xl font-semibold mb-2">{game.name}</h3>
                                    <p className="text-zinc-400">{game.description}</p>
                                </>
                            );
                            const cardClasses = "group block h-full rounded-2xl border p-6 transition-colors";

                            return (
                                <motion.div
                                    key={game.name}
                                    {...fadeUp}
                                    transition={{ ...fadeUp.transition, delay: index * 0.1 }}
                                >
                                    {game.active ? (
                                        <Link to={`/${game.slug}`} className={`${cardClasses} border-zinc-800 bg-zinc-950 hover:border-zinc-600`}>
                                            {content}
                                        </Link>
                                    ) : (
                                        <div className={`${cardClasses} border-zinc-900 bg-zinc-950/50 opacity-60`}>
                                            {content}
                                        </div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* --- BEHIND THE GAMES --- */}
            <section className="border-t border-zinc-900 py-24">
                <motion.div {...fadeUp} className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center gap-10">
                    <img
                        src={MyPhoto}
                        alt="Yash Jain"
                        className="w-40 h-40 md:w-48 md:h-48 object-cover rounded-full border border-zinc-800"
                    />
                    <div className="text-center md:text-left">
                        <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">Behind the games</p>
                        <h2 className="text-3xl font-extrabold tracking-tight mb-4">Hi, I'm Yash.</h2>
                        <p className="text-zinc-400 leading-relaxed">
                            I design and build every game on this site, usually because my friends and I wanted
                            something new to play. If a game makes your night better, or breaks halfway
                            through, I'd love to hear about it.
                        </p>
                        <div className="mt-6 flex gap-6 justify-center md:justify-start text-sm">
                            <Link to="/about" className="text-white hover:text-yellow transition-colors">More about me</Link>
                            <Link to="/contact" className="text-zinc-400 hover:text-white transition-colors">Get in touch</Link>
                        </div>
                    </div>
                </motion.div>
            </section>
        </div>
    );
};

export default Home;
